import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n";

const GoogleButton = ({ label }) => {
  const goGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <Button type="button" variant="outline" onClick={goGoogle} data-testid="google-auth-btn"
      className="w-full rounded-xl h-11 gap-2.5">
      <svg className="h-5 w-5" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1Z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84Z"/><path fill="#EA4335" d="M12 4.75c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 1.46 14.97.5 12 .5A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.3 9.14 4.75 12 4.75Z"/></svg>
      {label}
    </Button>
  );
};

const AuthShell = ({ children, title, subtitle }) => (
  <div className="relative min-h-screen flex items-center justify-center gp-grid-bg px-4">
    <div className="absolute top-5 left-5"><Link to="/"><Logo /></Link></div>
    <div className="absolute top-5 right-5"><ThemeToggle /></div>
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}
      className="w-full max-w-md rounded-3xl border border-border bg-card p-7 sm:p-9 shadow-xl">
      <h1 className="font-display text-2xl font-bold tracking-tight">{title}</h1>
      <p className="mt-1.5 text-sm text-muted-foreground">{subtitle}</p>
      <div className="mt-7">{children}</div>
    </motion.div>
  </div>
);

export function Login() {
  const { t } = useT();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || t("errors.generic"));
    } finally { setLoading(false); }
  };

  return (
    <AuthShell title={t("auth.loginTitle")} subtitle={t("auth.loginSubtitle")}>
      <GoogleButton label={t("auth.google")} />
      <div className="my-5 flex items-center gap-3 text-xs text-muted-foreground">
        <div className="h-px flex-1 bg-border" />{t("auth.orEmail")}<div className="h-px flex-1 bg-border" />
      </div>
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-1.5">
          <Label>{t("auth.email")}</Label>
          <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" className="h-11 rounded-xl" />
        </div>
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label>{t("auth.password")}</Label>
            <Link to="/forgot-password" className="text-xs text-brand-700 dark:text-brand-400" data-testid="forgot-link">{t("auth.forgot")}</Link>
          </div>
          <Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" className="h-11 rounded-xl" />
        </div>
        <Button type="submit" disabled={loading} data-testid="login-submit" className="w-full h-11 rounded-xl bg-brand-500 hover:bg-brand-600 text-black">
          {loading ? t("common.loading") : t("auth.login")}
        </Button>
      </form>
      <p className="mt-6 text-center text-sm text-muted-foreground">
        {t("auth.noAccount")} <Link to="/register" className="font-semibold text-brand-700 dark:text-brand-400">{t("auth.register")}</Link>
      </p>
    </AuthShell>
  );
}

export function Register() {
  const { t } = useT();
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(name, email, password);
      navigate("/onboarding");
    } catch (err) {
      toast.error(err.response?.data?.detail || t("errors.generic"));
    } finally { setLoading(false); }
  };

  return (
    <AuthShell title={t("auth.registerTitle")} subtitle={t("auth.registerSubtitle")}>
      <GoogleButton label={t("auth.google")} />
      <div className="my-5 flex items-center gap-3 text-xs text-muted-foreground">
        <div className="h-px flex-1 bg-border" />{t("auth.orEmail")}<div className="h-px flex-1 bg-border" />
      </div>
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-1.5">
          <Label>{t("auth.name")}</Label>
          <Input required value={name} onChange={(e) => setName(e.target.value)} data-testid="register-name" className="h-11 rounded-xl" />
        </div>
        <div className="space-y-1.5">
          <Label>{t("auth.email")}</Label>
          <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="register-email" className="h-11 rounded-xl" />
        </div>
        <div className="space-y-1.5">
          <Label>{t("auth.password")}</Label>
          <Input type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} data-testid="register-password" className="h-11 rounded-xl" />
        </div>
        <Button type="submit" disabled={loading} data-testid="register-submit" className="w-full h-11 rounded-xl bg-brand-500 hover:bg-brand-600 text-black">
          {loading ? t("common.loading") : t("auth.register")}
        </Button>
      </form>
      <p className="mt-6 text-center text-sm text-muted-foreground">
        {t("auth.haveAccount")} <Link to="/login" className="font-semibold text-brand-700 dark:text-brand-400">{t("auth.login")}</Link>
      </p>
    </AuthShell>
  );
}

export function ForgotPassword() {
  const { t } = useT();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const api = (await import("@/lib/api")).default;
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch { setSent(true); } finally { setLoading(false); }
  };

  return (
    <AuthShell title={t("auth.forgotTitle")} subtitle={t("auth.forgotSubtitle")}>
      {sent ? (
        <div className="space-y-5">
          <p className="rounded-xl bg-emerald-500/10 border border-emerald-500/20 p-4 text-sm text-emerald-700 dark:text-emerald-300" data-testid="reset-sent-msg">
            {t("auth.resetSent")}
          </p>
          <Link to="/login" className="block text-center text-sm font-semibold text-brand-700 dark:text-brand-400">{t("auth.backToLogin")}</Link>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label>{t("auth.email")}</Label>
            <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email" className="h-11 rounded-xl" />
          </div>
          <Button type="submit" disabled={loading} data-testid="forgot-submit" className="w-full h-11 rounded-xl bg-brand-500 hover:bg-brand-600 text-black">{loading ? t("common.loading") : t("auth.sendReset")}</Button>
          <Link to="/login" className="block text-center text-sm text-muted-foreground">{t("auth.backToLogin")}</Link>
        </form>
      )}
    </AuthShell>
  );
}

export function ResetPassword() {
  const { t } = useT();
  const navigate = useNavigate();
  const token = new URLSearchParams(useLocation().search).get("token") || "";
  const [valid, setValid] = useState(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    (async () => {
      if (!token) { setValid(false); return; }
      try {
        const api = (await import("@/lib/api")).default;
        const res = await api.get(`/auth/reset-password/validate?token=${encodeURIComponent(token)}`);
        setValid(Boolean(res.data.valid));
      } catch { setValid(false); }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { toast.error(t("auth.passwordMismatch")); return; }
    setLoading(true);
    try {
      const api = (await import("@/lib/api")).default;
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("errors.generic"));
    } finally { setLoading(false); }
  };

  return (
    <AuthShell title={t("auth.resetTitle")} subtitle={t("auth.resetSubtitle")}>
      {valid === null ? (
        <p className="text-sm text-muted-foreground">{t("common.loading")}</p>
      ) : done ? (
        <div className="space-y-5">
          <p className="rounded-xl bg-emerald-500/10 border border-emerald-500/20 p-4 text-sm text-emerald-700 dark:text-emerald-300" data-testid="reset-done-msg">{t("auth.resetDone")}</p>
          <Link to="/login" className="block text-center text-sm font-semibold text-brand-700 dark:text-brand-400">{t("auth.backToLogin")}</Link>
        </div>
      ) : !valid ? (
        <div className="space-y-5">
          <p className="rounded-xl bg-rose-500/10 border border-rose-500/20 p-4 text-sm text-rose-700 dark:text-rose-300" data-testid="reset-invalid-msg">{t("auth.resetInvalid")}</p>
          <Link to="/forgot-password" className="block text-center text-sm font-semibold text-brand-700 dark:text-brand-400" data-testid="reset-request-again">{t("auth.requestAgain")}</Link>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label>{t("auth.newPassword")}</Label>
            <Input type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} data-testid="reset-password" className="h-11 rounded-xl" />
          </div>
          <div className="space-y-1.5">
            <Label>{t("auth.confirmPassword")}</Label>
            <Input type="password" required minLength={8} value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="reset-password-confirm" className="h-11 rounded-xl" />
          </div>
          <Button type="submit" disabled={loading} data-testid="reset-submit" className="w-full h-11 rounded-xl bg-brand-500 hover:bg-brand-600 text-black">{loading ? t("common.loading") : t("auth.resetSubmit")}</Button>
        </form>
      )}
    </AuthShell>
  );
}
